/**
 * @file EmptyState.jsx
 * @description Boş durum bileşeni
 *
 * Henüz arama yapılmamış veya sonuç bulunamadığında gösterilir.
 * Emlakjet logosu, açıklama ve tıklanabilir örnek arama
 * önerileri içerir.
 *
 * @module widget/components/EmptyState
 */

import { Building2 } from "lucide-react";
import emlakjetLogo from "../logo.svg";

/**
 * Örnek arama prompt'ları
 *
 * Kullanıcıya arama önerileri sunan tıklanabilir metinler.
 * Tıklandığında sendFollowUpMessage ile ChatGPT'ye gönderilir.
 *
 * @constant {string[]}
 */
const EXAMPLE_PROMPTS = [
  "İstanbul Kadıköy'de satılık 3+1 daire",
  "Ankara'da kiralık 2+1, max 15.000 TL",
  "İzmir Karşıyaka'da denize yakın satılık daire",
];

/**
 * Boş Durum Bileşeni
 *
 * Widget ilk açıldığında veya arama sonucu boş olduğunda
 * kullanıcıya rehberlik sağlar. Örnek aramalar tıklandığında
 * ChatGPT'ye otomatik mesaj gönderilir.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {boolean} props.isDark - Karanlık mod aktif mi
 *
 * @returns {JSX.Element} Boş durum bileşeni
 *
 * @example
 * <EmptyState isDark={theme === 'dark'} />
 *
 * @remarks
 * window.openai.sendFollowUpMessage API'sini kullanarak
 * tıklanan örnek aramayı ChatGPT'ye iletir.
 */
export function EmptyState({ isDark }) {
  const handlePromptClick = (promptText) => {
    if (window.openai?.sendFollowUpMessage) {
      window.openai.sendFollowUpMessage({ prompt: promptText });
    }
  };

  return (
    <div className="p-5 sm:p-6">
      {/* Header */}
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center mb-4">
          <img src={emlakjetLogo} alt="Emlakjet" className="h-12 w-auto" />
        </div>
        <h3
          className={`text-base font-semibold mb-2 ${
            isDark ? "text-white" : "text-black"
          }`}
        >
          Emlakjet Arama
        </h3>
        <p
          className={`text-sm mb-6 max-w-xs mx-auto ${
            isDark ? "text-white/60" : "text-black/60"
          }`}
        >
          Türkiye genelinde satılık ve kiralık emlak ilanlarını arayın
        </p>
      </div>

      {/* Example Prompts */}
      <div className="space-y-2">
        <p
          className={`text-xs font-medium mb-3 ${
            isDark ? "text-white/40" : "text-black/40"
          }`}
        >
          Örnek aramalar
        </p>
        {EXAMPLE_PROMPTS.map((prompt) => (
          <div
            key={prompt}
            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
              isDark
                ? "bg-white/5 hover:bg-white/10"
                : "bg-black/5 hover:bg-black/10"
            }`}
            onClick={() => handlePromptClick(prompt)}
          >
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                isDark ? "bg-white/10" : "bg-black/10"
              }`}
            >
              <Building2 className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <span
              className={`text-sm ${
                isDark ? "text-white/80" : "text-black/80"
              }`}
            >
              {prompt}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmptyState;
